import { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { MoneyInput } from '../components/MoneyInput';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, Package, AlertTriangle, Calendar, Upload, FileSpreadsheet, X, Archive, ArchiveRestore, ShieldCheck, Merge } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ArticleMergePanel } from '../components/ArticleMergePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper para verificar permisos
const userCan = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.permissions) {
    return user.permissions[module]?.[action] === true;
  }
  return false;
};

// v3.40.9 — Regla restituida: ADMIN tiene acceso a TODO (bypass incluido).
// El permiso `articulos.estabilizar` sigue existiendo como permiso DELEGABLE
// para que usuarios NO-admin (ej. farmacéuticos) puedan operar Homologación
// & Fusión. Por default queda en OFF; el admin lo habilita manualmente a
// quien corresponda. Esta regla es un invariante del sistema (ver
// /app/memory/ARCHITECTURE_RULES.md).
const canEstabilizar = (user) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return !!user?.permissions?.articulos?.estabilizar;
};

function Articles() {
  const { user } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const UNITS = ['unidad', 'comprimido', 'ampolla', 'frasco', 'caja', 'sobre', 'ml', 'mg', 'g', 'kg', 'litro'];
  const [formData, setFormData] = useState({
    name: '',
    warehouse_id: '',
    stock: 0,
    reorder_point: 0,
    unit: 'unidad',
    expiration_date: '',
    current_cost_price: 0,
    is_medicamento: false,
    category: '',
    lot_number: ''
  });
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // dry-run result
  const [importDepoDefault, setImportDepoDefault] = useState('');
  const [exporting, setExporting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // article object pending delete
  const [deleting, setDeleting] = useState(false);
  const [showInitialStock, setShowInitialStock] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  
  // Filtro especial desde Dashboard
  const [specialFilter, setSpecialFilter] = useState(searchParams.get('filter') || '');
  
  // Permisos del usuario
  const canCreate = userCan(user, 'articulos', 'crear');
  const canView = userCan(user, 'articulos', 'ver');
  const canEdit = !!user && (user.role === 'admin' || user.role === 'supervisor');
  const canDelete = userCan(user, 'articulos', 'eliminar');
  const canExport = user?.role === 'admin' || user?.permissions?.exportaciones?.excel;
  const canStabilize = canEstabilizar(user);  // v3.40.1 · delegable sin ser admin
  // v3.41.0 — Tab inicial: respeta ?tab=stabilize cuando viene de la alerta
  // crítica del Dashboard. Fallback: catálogo si tiene ver, si no estabilización.
  const requestedTab = searchParams.get('tab');
  const defaultTab = (requestedTab === 'stabilize' && canStabilize)
    ? 'stabilize'
    : (canView ? 'catalog' : (canStabilize ? 'stabilize' : 'catalog'));
  
  // Estados para filtros (sólo campos de catálogo)
  const [filters, setFilters] = useState({
    name: '',
    category: '',
    is_medicamento: '',
    price: ''
  });
  
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [showExternals, setShowExternals] = useState(true);
  const [onlyPending, setOnlyPending] = useState(false);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [articlesRes, warehousesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/articles?include_merged=true`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/warehouses`, { withCredentials: true })
      ]);
      
      // Ordenar artículos alfabéticamente por nombre
      const sortedArticles = articlesRes.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
      );
      
      setArticles(sortedArticles);
      setFilteredArticles(sortedArticles);
      setWarehouses(warehousesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };
  
  // Aplicar filtros en tiempo real
  useEffect(() => {
    let filtered = [...articles];
    
    // Ocultar artículos archivados ("sin reposición") salvo que el toggle esté activo
    if (!showArchived) {
      filtered = filtered.filter(a => !a.is_archived);
    }

    // v3.44.0 — Toggles de catálogo unificado:
    //  * `showExternals=false` oculta las fichas de origen REM que AÚN están
    //    pendientes de homologación (external_origin=true && pending_catalog_review=true).
    //    Una vez que Farmacia homologa el alta, el artículo queda plenamente
    //    incorporado al catálogo y se muestra como cualquier otro aunque haya
    //    nacido por REM.
    //  * `onlyPending=true` deja sólo las fichas con pending_catalog_review=true,
    //    para que Farmacia pueda procesar la cola de homologación.
    if (!showExternals) {
      filtered = filtered.filter(a => !(a.external_origin && a.pending_catalog_review));
    }
    if (onlyPending) {
      filtered = filtered.filter(a => a.pending_catalog_review === true);
    }
    
    // Filtro especial desde Dashboard (stock bajo) - usa article.stock heredado
    if (specialFilter === 'low_stock') {
      filtered = filtered.filter(a => (a.stock || 0) <= (a.reorder_point || 0));
    }
    
    if (filters.name) {
      filtered = filtered.filter(a => 
        a.name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }
    
    if (filters.category) {
      filtered = filtered.filter(a => 
        (a.category || '').toLowerCase().includes(filters.category.toLowerCase())
      );
    }
    
    if (filters.is_medicamento === 'yes') {
      filtered = filtered.filter(a => a.is_medicamento === true);
    } else if (filters.is_medicamento === 'no') {
      filtered = filtered.filter(a => !a.is_medicamento);
    }
    
    if (filters.price) {
      filtered = filtered.filter(a => 
        (a.current_cost_price || 0).toString().includes(filters.price)
      );
    }
    
    setFilteredArticles(filtered);
  }, [filters, articles, specialFilter, showArchived, showExternals, onlyPending]);
  
  // Función para limpiar filtro especial
  const clearSpecialFilter = () => {
    setSpecialFilter('');
    setSearchParams({});
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let payload = { ...formData };
      // Si está creando y NO marcó stock inicial, mando solo catálogo:
      // warehouse_id obligado por el backend → uso el primero como "placeholder", stock=0.
      if (!editingArticle && !showInitialStock) {
        if (warehouses.length === 0) {
          toast.error('No hay depósitos disponibles. Cargá uno antes de crear artículos.');
          return;
        }
        payload = {
          ...payload,
          warehouse_id: warehouses[0].warehouse_id,
          stock: 0,
          reorder_point: 0,
          expiration_date: null,
          lot_number: null,
        };
      }
      if (editingArticle) {
        await axios.put(`${BACKEND_URL}/api/articles/${editingArticle.article_id}`, payload, { withCredentials: true });
        toast.success('Artículo actualizado');
      } else {
        await axios.post(`${BACKEND_URL}/api/articles`, payload, { withCredentials: true });
        toast.success('Artículo creado');
      }
      setDialogOpen(false);
      setEditingArticle(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Error al guardar artículo');
    }
  };
  
  const handleEdit = (article) => {
    setEditingArticle(article);
    setShowInitialStock(false);
    setFormData({
      name: article.name,
      warehouse_id: article.warehouse_id || '',
      stock: article.stock || 0,
      reorder_point: article.reorder_point || 0,
      unit: article.unit || 'unidad',
      expiration_date: article.expiration_date || '',
      current_cost_price: article.current_cost_price || 0,
      is_medicamento: !!article.is_medicamento,
      category: article.category || '',
      lot_number: ''  // El lote se asocia a stock_lots; al editar no se toca
    });
    setDialogOpen(true);
  };

  const handleToggleArchive = async (article) => {
    const next = !article.is_archived;
    try {
      await axios.patch(
        `${BACKEND_URL}/api/articles/${article.article_id}/archive`,
        { is_archived: next },
        { withCredentials: true }
      );
      toast.success(next ? 'Artículo archivado (sin reposición)' : 'Artículo desarchivado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo cambiar el estado de archivado');
    }
  };
  
  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/articles/${confirmDelete.article_id}`, { withCredentials: true });
      toast.success('Artículo eliminado');
      setConfirmDelete(null);
      fetchData();
    } catch (error) {
      console.error('Error eliminando artículo:', error);
      const errorMessage = error.response?.data?.detail || 'Error al eliminar artículo';
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };
  
  const resetForm = () => {
    setEditingArticle(null);
    setShowInitialStock(false);
    setFormData({
      name: '',
      warehouse_id: '',
      stock: 0,
      reorder_point: 0,
      unit: 'unidad',
      expiration_date: '',
      current_cost_price: 0,
      is_medicamento: false,
      category: '',
      lot_number: ''
    });
  };
  
  const handleImportPreview = async () => {
    if (!importFile) {
      toast.error('Seleccioná un archivo Excel/CSV primero');
      return;
    }
    setImporting(true);
    setImportPreview(null);
    const fd = new FormData();
    fd.append('file', importFile);
    const params = new URLSearchParams({ dry_run: 'true' });
    if (importDepoDefault) params.append('deposito_default', importDepoDefault);
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/articles/import?${params.toString()}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setImportPreview(resp.data);
      if (resp.data.rows_with_errors > 0) {
        toast.warning(`${resp.data.rows_with_errors} fila(s) con errores. Revisá antes de importar.`);
      } else {
        toast.success(`Vista previa OK: ${resp.data.articles_to_create} artículos nuevos, ${resp.data.lots_to_create} lotes.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en vista previa');
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Seleccioná un archivo Excel/CSV primero');
      return;
    }
    setImporting(true);
    const fd = new FormData();
    fd.append('file', importFile);
    const params = new URLSearchParams();
    if (importDepoDefault) params.append('deposito_default', importDepoDefault);
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/articles/import${params.toString() ? '?' + params.toString() : ''}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const d = resp.data;
      const msg = `${d.articles_created} artículos nuevos + ${d.articles_updated} actualizados + ${d.lots_created} lotes`;
      if (d.errors?.length) {
        toast.warning(`${msg}. ${d.errors.length} errores: ver consola.`);
        console.warn('Errores import:', d.errors);
      } else {
        toast.success(msg);
      }
      setImportDialogOpen(false);
      setImportFile(null);
      setImportPreview(null);
      setImportDepoDefault('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar');
    } finally {
      setImporting(false);
    }
  };
  
  const downloadTemplate = async () => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/articles/template`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_articulos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No pude descargar la plantilla');
    }
  };
  
  const exportToExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/articles`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Crear URL del blob y forzar descarga
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      
      // Crear elemento anchor y simular click
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `articulos_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      
      // Limpiar después de un pequeño delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      
      toast.success('Artículos exportados a Excel');
    } catch (error) {
      console.error('Error exportando:', error);
      toast.error('Error al exportar artículos');
    } finally {
      setExporting(false);
    }
  };
  
  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w.warehouse_id === warehouseId);
    return warehouse?.name || 'N/A';
  };
  
  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }
  
  return (
    <div className="space-y-6" data-testid="articles-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-2">Artículos (Catálogo)</h1>
        <p className="text-muted-foreground">Un solo catálogo. Alta / edición, homologación de altas por REM y fusión de duplicados conviven acá.</p>

        {/* Indicador de filtro activo */}
        {specialFilter === 'low_stock' && (
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-amber-500 text-white flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Mostrando solo artículos con Stock Bajo
              <button onClick={clearSpecialFilter} className="ml-1 hover:bg-amber-600 rounded-full p-0.5">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          </div>
        )}
      </div>

      <Tabs defaultValue={defaultTab} className="space-y-4">
        <TabsList data-testid="articles-tabs">
          {canView && (
            <TabsTrigger value="catalog" data-testid="tab-catalog">
              <Package className="w-4 h-4 mr-1" /> Catálogo
            </TabsTrigger>
          )}
          {canStabilize && (
            <TabsTrigger value="stabilize" data-testid="tab-stabilize">
              <ShieldCheck className="w-4 h-4 mr-1" /> Homologación &amp; Fusión
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="catalog" className="space-y-4">
          <div className="flex items-center justify-end gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none" data-testid="toggle-show-externals">
              <input
                type="checkbox"
                checked={showExternals}
                onChange={(e) => setShowExternals(e.target.checked)}
                className="rounded border-input h-4 w-4"
              />
              Mostrar externos pendientes (REM)
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none" data-testid="toggle-only-pending">
              <input
                type="checkbox"
                checked={onlyPending}
                onChange={(e) => setOnlyPending(e.target.checked)}
                className="rounded border-input h-4 w-4"
              />
              Solo pendientes de homologación
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none mr-2" data-testid="toggle-show-archived">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="rounded border-input h-4 w-4"
              />
              Mostrar archivados
            </label>
          {canExport && (
            <Button variant="outline" onClick={exportToExcel} disabled={exporting} data-testid="export-articles-button">
              {exporting ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : (
                <FileSpreadsheet className="w-4 h-4 mr-2" />
              )}
              Exportar Excel
            </Button>
          )}
          
          {/* Importador de Excel — DESHABILITADO (v3.22.2).
              La carga inicial ya fue hecha y los archivos históricos generaban
              lotes con expiration_date='NaT' (pandas). Si en el futuro se
              necesita reabilitar, quitar el `false &&` de la condición. */}
          {false && canCreate && (
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-articles-button">
                  <Upload className="w-4 h-4 mr-2" />
                  Importar Excel
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Importar Artículos + Lotes desde Excel</DialogTitle>
                <DialogDescription>
                  Soporta planillas con múltiples filas por artículo (cada fila = un lote). Compatible con tu planilla actual de medicación.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="bg-muted/50 p-4 rounded-md space-y-2">
                  <p className="text-sm font-medium">Columnas aceptadas (no son case-sensitive):</p>
                  <ul className="text-xs space-y-1 text-muted-foreground">
                    <li>• <strong>nombre</strong> (o <code>medicacion</code>) — <em>obligatoria</em></li>
                    <li>• <strong>cantidad</strong> (o <code>stock</code>) — <em>obligatoria</em></li>
                    <li>• <strong>deposito</strong> — opcional si elegís uno por defecto abajo</li>
                    <li>• <strong>lote</strong> (o <code>numero_lote</code>) — obligatorio para medicamentos</li>
                    <li>• <strong>vencimiento</strong> (o <code>fecha_vencimiento</code>) — DD/MM/AAAA o fecha de Excel</li>
                    <li>• <strong>es_medicamento</strong> — Sí/No (si se omite, se deduce de la presencia de lote+vencimiento)</li>
                    <li>• <strong>categoria</strong> — opcional</li>
                    <li>• <strong>precio_costo</strong>, <strong>punto_reposicion</strong> — opcional</li>
                  </ul>
                  <p className="text-xs mt-2 text-muted-foreground"><strong>Tip:</strong> Si tu planilla tiene 3 filas de Omeprazol con lotes distintos, el sistema crea UN artículo con 3 lotes asociados.</p>
                </div>
                
                <div>
                  <Label htmlFor="depo-default">Depósito por defecto (opcional)</Label>
                  <select
                    id="depo-default"
                    className="w-full h-9 mt-1 rounded-md border border-input bg-background px-3 text-sm"
                    value={importDepoDefault}
                    onChange={(e) => setImportDepoDefault(e.target.value)}
                    data-testid="import-depo-default"
                  >
                    <option value="">(Usar columna 'deposito' del Excel)</option>
                    {warehouses.map(w => (
                      <option key={w.warehouse_id} value={w.name}>{w.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">Se aplica solo a filas que NO tengan columna depósito.</p>
                </div>

                <div>
                  <Label htmlFor="excel-file">Archivo (.xlsx, .xls, .csv)</Label>
                  <Input
                    id="excel-file"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    data-testid="import-file-input"
                    onChange={(e) => { setImportFile(e.target.files[0]); setImportPreview(null); }}
                    className="mt-1"
                  />
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadTemplate}
                  className="w-full"
                  data-testid="download-template-button"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Descargar Plantilla Excel (con ejemplos)
                </Button>

                {importPreview && (
                  <div className="border rounded-md p-3 bg-blue-50/40 dark:bg-blue-950/20 text-sm space-y-1" data-testid="import-preview">
                    <p className="font-semibold">Vista previa</p>
                    <p>• Filas leídas: <strong>{importPreview.rows_total}</strong></p>
                    <p>• Artículos nuevos a crear: <strong>{importPreview.articles_to_create}</strong></p>
                    <p>• Artículos existentes a actualizar: <strong>{importPreview.articles_existing_reuse}</strong></p>
                    <p>• Lotes a crear: <strong>{importPreview.lots_to_create}</strong></p>
                    {importPreview.rows_with_errors > 0 && (
                      <p className="text-rose-600">• Errores: <strong>{importPreview.rows_with_errors}</strong></p>
                    )}
                    {importPreview.errors?.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs">Ver errores</summary>
                        <ul className="mt-1 text-xs ml-4 list-disc max-h-32 overflow-y-auto">
                          {importPreview.errors.slice(0, 20).map((er, i) => <li key={i}>{er}</li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
              
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={handleImportPreview}
                  disabled={!importFile || importing}
                  data-testid="import-preview-button"
                >
                  Vista previa
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={!importFile || importing}
                  data-testid="import-submit-button"
                >
                  {importing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Importando...
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Importar
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
          
          {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-article-button">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Artículo
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingArticle ? 'Editar Artículo (Catálogo)' : 'Nuevo Artículo (Catálogo)'}</DialogTitle>
              <DialogDescription>
                {editingArticle
                  ? 'Modificá los datos del catálogo. Para gestionar stock, lotes y vencimientos usá → Stock por Depósito y Recepciones.'
                  : 'Definí el artículo en el catálogo. El stock vivo se ingresa por Recepciones; opcionalmente podés cargar stock inicial al pie.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* ============== CATÁLOGO ============== */}
              <div className="rounded-md border bg-muted/30 p-4 space-y-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">📚 Datos del catálogo</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="name">Nombre *</Label>
                    <Input id="name" data-testid="article-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                  <div>
                    <Label htmlFor="category">Categoría</Label>
                    <Input
                      id="category"
                      list="article-categories"
                      placeholder="Ej: Medicamentos, Insumos..."
                      value={formData.category}
                      onChange={(e) => {
                        const v = e.target.value;
                        const isMed = v.trim().toLowerCase() === 'medicamentos';
                        setFormData({ ...formData, category: v, is_medicamento: isMed || formData.is_medicamento });
                      }}
                      data-testid="article-category-input"
                    />
                    <datalist id="article-categories">
                      <option value="Medicamentos" />
                      <option value="Insumos" />
                      <option value="Descartables" />
                      <option value="Limpieza" />
                      <option value="Oficina" />
                    </datalist>
                  </div>
                  <div>
                    <Label htmlFor="unit">Unidad de medida</Label>
                    <select
                      id="unit"
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="article-unit-input"
                    >
                      {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="cost_price">Precio de Costo (referencia)</Label>
                    <MoneyInput id="cost_price" data-testid="article-price-input" value={formData.current_cost_price} onChange={(e) => setFormData({ ...formData, current_cost_price: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div className="flex items-center gap-2 md:col-span-2">
                    <input
                      id="is_medicamento"
                      type="checkbox"
                      checked={formData.is_medicamento}
                      onChange={(e) => setFormData({ ...formData, is_medicamento: e.target.checked })}
                      data-testid="article-is-medicamento"
                      className="h-4 w-4"
                    />
                    <Label htmlFor="is_medicamento" className="cursor-pointer">
                      Es Medicamento (exige Nº de Lote en cada recepción)
                    </Label>
                  </div>
                </div>
              </div>

              {/* v3.28.21 · Punto de Reposición — propiedad del artículo, siempre visible */}
              <div className="rounded-md border p-4 space-y-2 bg-amber-50/40 dark:bg-amber-950/10">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label htmlFor="reorder_point_top" className="text-sm font-semibold">Punto de Reposición</Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Cantidad mínima antes de generar alerta de stock bajo. 0 = sin alerta.</p>
                  </div>
                  <Input id="reorder_point_top" type="number" min="0"
                         className="w-32 text-right"
                         data-testid="article-reorder-input-top"
                         value={formData.reorder_point}
                         onChange={(e) => setFormData({ ...formData, reorder_point: parseInt(e.target.value) || 0 })} />
                </div>
              </div>

              {/* ============== STOCK INICIAL (opcional, sólo al crear) ============== */}
              {!editingArticle && (
                <div className="rounded-md border border-dashed p-4 space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showInitialStock}
                      onChange={(e) => {
                        setShowInitialStock(e.target.checked);
                        if (!e.target.checked) {
                          setFormData({ ...formData, stock: 0, warehouse_id: '', expiration_date: '', lot_number: '', reorder_point: 0 });
                        }
                      }}
                      className="h-4 w-4"
                      data-testid="toggle-initial-stock"
                    />
                    <span className="text-sm font-semibold">¿Querés cargar stock inicial? <span className="text-muted-foreground font-normal">(opcional — lo normal es cargarlo por Recepciones)</span></span>
                  </label>
                  {showInitialStock && (
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                      <div>
                        <Label htmlFor="warehouse">Depósito destino *</Label>
                        <select
                          id="warehouse"
                          data-testid="article-warehouse-select"
                          value={formData.warehouse_id}
                          onChange={(e) => setFormData({ ...formData, warehouse_id: e.target.value })}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          required={showInitialStock}
                        >
                          <option value="">Selecciona depósito</option>
                          {warehouses.map(w => (
                            <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="stock">Cantidad inicial</Label>
                        <Input id="stock" type="number" data-testid="article-stock-input" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) || 0 })} min="0" />
                      </div>
                      <div>
                        <Label htmlFor="expiration_date">Fecha de Vencimiento</Label>
                        <Input id="expiration_date" type="date" min={new Date().toISOString().slice(0,10)} data-testid="article-expiration-input" value={formData.expiration_date} onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })} />
                      </div>
                      {formData.is_medicamento && parseInt(formData.stock) > 0 && (
                        <div className="col-span-2 p-3 rounded-md border border-amber-400/60 bg-amber-50/40 dark:bg-amber-950/20">
                          <Label htmlFor="lot_number">
                            Nº de Lote inicial <span className="text-rose-600">*</span>
                          </Label>
                          <Input
                            id="lot_number"
                            value={formData.lot_number}
                            onChange={(e) => setFormData({ ...formData, lot_number: e.target.value })}
                            placeholder="Ej: A0605 / 298X5 / 390"
                            data-testid="article-lot-number-input"
                            required={showInitialStock && formData.is_medicamento && parseInt(formData.stock) > 0}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Se creará un lote trazable con esta etiqueta. Las próximas entradas van por Recepciones.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {editingArticle && (
                <div className="rounded-md border-l-4 border-blue-500 bg-blue-50/40 dark:bg-blue-950/20 p-3 text-xs text-blue-800 dark:text-blue-200">
                  💡 Para modificar stock, lotes o vencimientos de este artículo, andá a <strong>Stock por Depósito</strong> o registrá una <strong>Recepción</strong>.
                </div>
              )}

              <DialogFooter>
                <Button type="submit" data-testid="article-submit-button">{editingArticle ? 'Actualizar' : 'Crear'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
          )}
        </div>
      
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Unidad</TableHead>
                <TableHead className="text-center">Medicamento</TableHead>
                <TableHead className="text-right">Precio Costo (ref.)</TableHead>
                {(canEdit || canDelete) && <TableHead className="text-right">Acciones</TableHead>}
              </TableRow>
              <TableRow className="bg-muted/50">
                <TableHead className="py-2">
                  <Input
                    placeholder="Buscar nombre..."
                    value={filters.name}
                    onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                    className="h-8 text-sm"
                  />
                </TableHead>
                <TableHead className="py-2"></TableHead>
                <TableHead className="py-2">
                  <Input
                    placeholder="Categoría..."
                    value={filters.category}
                    onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                    className="h-8 text-sm"
                  />
                </TableHead>
                <TableHead className="py-2"></TableHead>
                <TableHead className="py-2">
                  <select
                    value={filters.is_medicamento}
                    onChange={(e) => setFilters({ ...filters, is_medicamento: e.target.value })}
                    className="w-full h-8 text-sm rounded-md border border-input bg-background px-2"
                  >
                    <option value="">(Todos)</option>
                    <option value="yes">Sí</option>
                    <option value="no">No</option>
                  </select>
                </TableHead>
                <TableHead className="py-2">
                  <Input
                    placeholder="Precio..."
                    value={filters.price}
                    onChange={(e) => setFilters({ ...filters, price: e.target.value })}
                    className="h-8 text-sm"
                  />
                </TableHead>
                {(canEdit || canDelete) && <TableHead className="py-2"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredArticles.map((article) => (
                <TableRow key={article.article_id} data-testid="article-row">
                  <TableCell className="font-medium">
                    <div className="flex items-center space-x-2">
                      <Package className="w-4 h-4 text-muted-foreground" />
                      <span>{article.name}</span>
                      {article.is_archived && (
                        <Badge variant="outline" className="text-xs ml-2 bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-300" title="Sin reposición — utilizar hasta agotar" data-testid={`archived-badge-${article.article_id}`}>
                          <Archive className="w-3 h-3 mr-1" /> Archivado
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {article.external_origin ? (
                        <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700 border-rose-300" title="Ficha nacida por recepción externa (REM) — medicación/insumos que trae la obra social del residente">
                          REM-OS
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300" title="Ficha de compra propia del geriátrico">
                          Comprado
                        </Badge>
                      )}
                      {article.pending_catalog_review && (
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-300" title="Pendiente de homologar por Farmacia — no se puede incluir en pedidos de compra" data-testid={`pending-badge-${article.article_id}`}>
                          Pendiente
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {article.category ? (
                      <Badge variant="outline">{article.category}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{article.unit || 'unidad'}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    {article.is_medicamento ? (
                      <Badge className="bg-rose-100 text-rose-700 border-rose-300">💊 Sí</Badge>
                    ) : (
                      <span className="text-muted-foreground">No</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    ${(article.current_cost_price || 0).toFixed(2)}
                  </TableCell>
                  {(canEdit || canDelete) && (
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        {canEdit && (
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(article)} data-testid="edit-article-button">
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleArchive(article)}
                            data-testid="archive-article-button"
                            title={article.is_archived ? 'Desarchivar' : 'Archivar (sin reposición)'}
                          >
                            {article.is_archived
                              ? <ArchiveRestore className="w-4 h-4 text-emerald-600" />
                              : <Archive className="w-4 h-4 text-slate-500" />}
                          </Button>
                        )}
                        {canDelete && (
                          <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(article)} data-testid="delete-article-button">
                            <Trash2 className="w-4 h-4 text-rose-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {articles.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay artículos registrados. Crea uno para comenzar.</p>
            </div>
          )}

          <div className="px-4 py-3 border-t bg-muted/30 text-xs text-muted-foreground">
            💡 El stock por depósito, lotes y vencimientos se gestiona en <strong>Stock por Depósito</strong> y <strong>Recepciones</strong>.
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="stabilize" className="space-y-4">
          <ArticleMergePanel onMergeComplete={fetchData} />
        </TabsContent>
      </Tabs>

      {/* Confirm delete dialog */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-article-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar artículo</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar el artículo <strong>{confirmDelete?.name}</strong>?
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-article">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-rose-600 text-white hover:bg-rose-700"
              data-testid="confirm-delete-article"
            >
              {deleting ? 'Eliminando…' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Articles;